import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import saveSurveyResult from '../utils/saveSurveyResult'
import createSurveyFaceRecordingManager from '../utils/createSurveyFaceRecordingManager'
import './genZMkSurvey.css'

const SURVEY_CODE = 'gen_z_mk_neuro'
const ADS = [
  { id: 'coke', name: 'Реклама 1', url: 'https://ai.surveyneuro.com/images/1-coke.mp4' },
  { id: 'telekom', name: 'Реклама 2', url: 'https://ai.surveyneuro.com/images/2-telekom.mp4' },
]
const POINTS = [
  [10, 25], [50, 25], [90, 25],
  [10, 55], [50, 55], [90, 55],
  [10, 85], [50, 85], [90, 85],
]
const CLICKS_PER_POINT = 5
const IMPLICIT_DEADLINE_MS = 1800
const IMPLICIT_ATTRIBUTES = [
  { label: 'Автентичен', words: ['Автентичен', 'Искрен', 'Вистински'] },
  { label: 'Модерен', words: ['Модерен', 'Современ', 'Иновативен'] },
  { label: 'Достоен за доверба', words: ['Достоен за доверба', 'Веродостоен', 'Сигурен'] },
]
const PLATFORMS = ['TikTok', 'Instagram', 'YouTube', 'Snapchat', 'Twitch', 'X']
const TOPICS = ['Меми', 'Упатства/„направи сам“', 'Видеоигри', 'Фитнес', 'Мода/убавина', 'Технологија', 'Вести', 'Политика/активизам', 'Храна']
const SOURCES = ['Пријатели', 'Инфлуенсери', 'Канали на брендовите', 'Новинари/Медиуми', 'Рецензии']
const AD_QUESTIONS = [
  'Рекламата ми беше интересна.',
  'Пораката беше јасна.',
  'Рекламата ми делуваше искрено/автентично.',
  'Оваа реклама предизвика емоции кај мене.',
  'Оваа реклама ја зајакнува мојата доверба во брендот.',
  'По гледањето на рекламата, поверојатно е да го купам/испробам рекламираниот производ или услуга.',
]
const BELIEF_QUESTIONS = [
  'Одржливоста влијае врз моите одлуки за купување.',
  'Претпочитам брендови што заземаат став за општествени прашања.',
  'Препораките од инфлуенсери се веродостојни.',
  'Ми е важно како брендовите ги користат моите податоци.',
]
const choice = (value, choices) => choices.includes(value)
const shuffle = (items) => [...items].sort(() => Math.random() - 0.5)
const makeResponseId = () => `mk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
const average = (values) => values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : null

function summarizeImplicit(trials) {
  const valid = trials.filter((trial) => !trial.timed_out)
  return {
    trial_count: trials.length,
    on_time_count: valid.length,
    mean_on_time_rt_ms: average(valid.map((trial) => trial.rt_ms)),
    attributes: Object.fromEntries(IMPLICIT_ATTRIBUTES.map((attribute) => {
      const group = trials.filter((trial) => trial.attribute === attribute.label)
      const onTime = group.filter((trial) => !trial.timed_out)
      return [attribute.label, {
        match_count: group.filter((trial) => trial.response === 'matches').length,
        trial_count: group.length,
        mean_on_time_rt_ms: average(onTime.map((trial) => trial.rt_ms)),
      }]
    })),
  }
}

function Scale({ questions, answers, onChange, prefix }) {
  return <div className="mk-matrix">
    <p className="mk-help">1 = Воопшто не се согласувам · 7 = Целосно се согласувам</p>
    {questions.map((question, index) => <fieldset key={question} className="mk-fieldset">
      <legend>{prefix}{questions.length === 1 ? '' : index + 1}. {question}</legend>
      <div className="mk-scale">{[1, 2, 3, 4, 5, 6, 7].map((number) => <label key={number} className={Number(answers[index]) === number ? 'selected' : ''}><input type="radio" name={`${prefix}-${index}`} checked={Number(answers[index]) === number} onChange={() => onChange(index, number)} />{number}</label>)}</div>
    </fieldset>)}
  </div>
}

export default function GenZMkSurvey() {
  const responseId = useMemo(() => new URLSearchParams(window.location.search).get('responseId') || makeResponseId(), [])
  const adOrder = useMemo(() => shuffle(ADS), [])
  const faceRecording = useMemo(() => createSurveyFaceRecordingManager({ surveyCode: SURVEY_CODE, responseId }), [responseId])
  const implicitTrialSets = useMemo(() => adOrder.map(() => shuffle(IMPLICIT_ATTRIBUTES.flatMap((attribute, attributeIndex) => attribute.words.map((word) => ({ attribute: attribute.label, attribute_index: attributeIndex + 1, stimulus: word }))))), [adOrder])
  const [phase, setPhase] = useState('consent')
  const [consent, setConsent] = useState({ age: '', camera: '', privacy: '' })
  const [demographics, setDemographics] = useState({ country: 'MK', age: '', gender: '', population: '', status: '' })
  const [habits, setHabits] = useState({ minutes: {}, shortVideos: '', topics: [], socialPurchase: '', onlinePercent: 50 })
  const [adIndex, setAdIndex] = useState(0)
  const [videoStarted, setVideoStarted] = useState(false)
  const [adAnswers, setAdAnswers] = useState({ scales: {}, share: '', impression: '' })
  const [adResults, setAdResults] = useState([])
  const [implicitIndex, setImplicitIndex] = useState(0)
  const [implicitResults, setImplicitResults] = useState([])
  const [implicitLate, setImplicitLate] = useState(false)
  const [beliefs, setBeliefs] = useState({ scales: {}, ranking: SOURCES })
  const [error, setError] = useState('')
  const [cameraState, setCameraState] = useState('idle')
  const [faceSeen, setFaceSeen] = useState(false)
  const [calibrationCounts, setCalibrationCounts] = useState(POINTS.map(() => 0))
  const [recalibration, setRecalibration] = useState(false)
  const trackerRef = useRef(null)
  const startedRef = useRef(false)
  const samplesRef = useRef([])
  const exposureRef = useRef(null)
  const exposuresRef = useRef([])
  const needsRecalibrationRef = useRef(false)
  const calibrationRef = useRef([])
  const videoRef = useRef(null)
  const implicitStartRef = useRef(0)
  const implicitLockedRef = useRef(false)
  const implicitTimerRef = useRef(null)
  const currentAd = adOrder[adIndex]
  const currentImplicitTrials = implicitTrialSets[adIndex]

  const stopTracker = useCallback(() => {
    faceRecording.abort()
    if (!startedRef.current) return
    try { trackerRef.current?.clearGazeListener() } catch (e) { console.warn(e) }
    try { trackerRef.current?.stopVideo() } catch (e) { console.warn(e) }
    try { trackerRef.current?.end() } catch (e) { console.warn(e) }
    startedRef.current = false
  }, [faceRecording])
  useEffect(() => () => stopTracker(), [stopTracker])
  useEffect(() => () => window.clearTimeout(implicitTimerRef.current), [])

  useEffect(() => {
    if (phase !== 'implicit-stimulus') return undefined
    implicitLockedRef.current = false
    implicitStartRef.current = performance.now()
    setImplicitLate(false)
    const deadline = window.setTimeout(() => setImplicitLate(true), IMPLICIT_DEADLINE_MS)
    return () => window.clearTimeout(deadline)
  }, [phase, implicitIndex])

  const beginCamera = async (isRecalibration = false) => {
    setError('')
    setRecalibration(isRecalibration)
    setCalibrationCounts(POINTS.map(() => 0))
    setCameraState('loading')
    setPhase('camera-check')
    try {
      const module = await import('../webgazer/webgazer')
      const tracker = module.default
      trackerRef.current = tracker
      if (!tracker.detectCompatibility()) throw new Error('unsupported camera')
      if (!startedRef.current) {
        tracker.saveDataAcrossSessions(false).setRegression('ridge').setTracker('TFFacemesh').applyKalmanFilter(true).showVideoPreview(true).showPredictionPoints(false).setGazeListener((data) => {
          const face = tracker.getTracker()?.getPositions?.()
          setFaceSeen(Array.isArray(face) && face.length > 0)
          const exposure = exposureRef.current
          if (!exposure || !data || !Number.isFinite(data.x) || !Number.isFinite(data.y) || samplesRef.current.length >= 5000) return
          samplesRef.current.push({ x: Math.round(data.x * 100) / 100, y: Math.round(data.y * 100) / 100, t_ms: Math.round(performance.now() - exposure.performanceStart) })
        })
        await tracker.clearData()
        startedRef.current = true
        await tracker.begin()
        tracker.removeMouseEventListeners()
      } else {
        await tracker.resume()
      }
      tracker.setVideoViewerSize(300, 225)
      tracker.showVideoPreview(true)
      setCameraState('ready')
    } catch (e) {
      stopTracker()
      setCameraState('error')
      setError('Камерата или следењето на погледот не може да се вклучи. Проверете ја дозволата за камера и обидете се повторно.')
    }
  }

  const startCalibration = () => {
    trackerRef.current?.showVideoPreview(false).showPredictionPoints(false)
    setPhase('calibration')
  }

  const clickPoint = (index, event) => {
    if (!faceSeen || calibrationCounts[index] >= CLICKS_PER_POINT) return
    const box = event.currentTarget.getBoundingClientRect()
    trackerRef.current?.recordScreenPosition(box.left + box.width / 2, box.top + box.height / 2, 'click')
    const next = [...calibrationCounts]
    next[index] += 1
    setCalibrationCounts(next)
    if (next.every((count) => count >= CLICKS_PER_POINT)) {
      calibrationRef.current.push({ completed_at: new Date().toISOString(), points: POINTS.length, clicks_per_point: CLICKS_PER_POINT, after_ad: recalibration ? adIndex : null })
      trackerRef.current?.showVideoPreview(false).showPredictionPoints(false)
      setCameraState('done')
      setPhase(recalibration ? 'neutral' : 'demographics')
    }
  }

  const validateConsent = () => {
    if (Object.values(consent).some((answer) => !answer)) return setError('Одговорете на сите задолжителни прашања.')
    if (Object.values(consent).some((answer) => answer === 'no')) return setPhase('ineligible')
    beginCamera()
  }
  const validateDemographics = () => {
    if (!demographics.age || !demographics.gender || !demographics.population || !demographics.status) return setError('Пополнете ги сите прашања.')
    const age = Number(demographics.age)
    if (!Number.isInteger(age) || age < 18 || age > 29) return setError('Ова истражување е наменето за лица на возраст од 18 до 29 години.')
    setError(''); setPhase('habits')
  }
  const validateHabits = () => {
    if (PLATFORMS.some((platform) => habits.minutes[platform] === undefined || habits.minutes[platform] === '') || !habits.shortVideos || !habits.socialPurchase) return setError('Пополнете ги сите задолжителни прашања.')
    if (PLATFORMS.some((platform) => Number(habits.minutes[platform]) < 0 || Number(habits.minutes[platform]) > 1440)) return setError('Внесете од 0 до 1440 минути за секоја платформа.')
    setError(''); setPhase('ad-intro')
  }
  const beginAd = async () => {
    setError('')
    try {
      await trackerRef.current?.resume?.()
      faceRecording.prepare()
      setVideoStarted(false)
      setPhase('ad')
    } catch (e) { setError('Следењето на погледот не може да продолжи. Обидете се повторно.') }
  }
  const onAdPlay = () => {
    if (exposureRef.current) return
    try { faceRecording.start() } catch (recordingError) {
      videoRef.current?.pause()
      faceRecording.abort()
      setError('Снимањето на камерата не може да започне. Проверете ја дозволата за камера и обидете се повторно.')
      setPhase('ad-intro')
      return
    }
    setVideoStarted(true)
    samplesRef.current = []
    exposureRef.current = { advertisement_id: currentAd.id, advertisement_url: currentAd.url, started_at: new Date().toISOString(), performanceStart: performance.now(), viewport: { width: window.innerWidth, height: window.innerHeight } }
  }
  const finishAd = async () => {
    const exposure = exposureRef.current
    if (!exposure) return
    exposureRef.current = null
    const durationMs = Math.round(performance.now() - exposure.performanceStart)
    needsRecalibrationRef.current = samplesRef.current.length < Math.max(10, Math.ceil(durationMs / 1000) * 2)
    const exposureResult = { advertisement_id: exposure.advertisement_id, advertisement_number: adIndex + 1, advertisement_url: exposure.advertisement_url, started_at: exposure.started_at, ended_at: new Date().toISOString(), duration_ms: durationMs, viewport: exposure.viewport, gaze_samples: samplesRef.current }
    faceRecording.finish(exposureResult)
    trackerRef.current?.pause?.()
    setPhase('recording-saving')
    try {
      await faceRecording.ensureUploaded([exposureResult])
      exposuresRef.current.push(exposureResult)
      setPhase('implicit-intro')
    } catch (recordingError) {
      setError('Снимката од камерата не може да се зачува. Проверете ја врската и пуштете ја рекламата повторно.')
      setPhase('ad-intro')
    }
  }
  const showImplicitTrial = () => {
    setPhase('implicit-fixation')
    implicitTimerRef.current = window.setTimeout(() => setPhase('implicit-stimulus'), 300)
  }
  const beginImplicitTest = () => {
    setImplicitIndex(0)
    setImplicitResults([])
    showImplicitTrial()
  }
  const respondImplicit = (response) => {
    if (phase !== 'implicit-stimulus' || implicitLockedRef.current) return
    implicitLockedRef.current = true
    const rtMs = Math.round(performance.now() - implicitStartRef.current)
    setImplicitResults((previous) => [...previous, { ...currentImplicitTrials[implicitIndex], response, rt_ms: rtMs, timed_out: rtMs > IMPLICIT_DEADLINE_MS }])
    setPhase('implicit-gap')
    implicitTimerRef.current = window.setTimeout(() => {
      if (implicitIndex + 1 >= currentImplicitTrials.length) setPhase('ad-questions')
      else { setImplicitIndex(implicitIndex + 1); showImplicitTrial() }
    }, 300)
  }
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code === 'KeyE') respondImplicit('does_not_match')
      if (event.code === 'KeyI') respondImplicit('matches')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })
  const saveAdQuestions = () => {
    if (Object.keys(adAnswers.scales).length !== AD_QUESTIONS.length || !choice(adAnswers.share, ['yes', 'no']) || !adAnswers.impression.trim()) return setError('Одговорете на сите прашања за рекламата.')
    setAdResults((previous) => [...previous, { advertisement_id: currentAd.id, advertisement_url: currentAd.url, answers: adAnswers, implicit_trials: implicitResults, implicit_summary: summarizeImplicit(implicitResults) }])
    setAdAnswers({ scales: {}, share: '', impression: '' }); setError('')
    if (adIndex + 1 < adOrder.length) {
      setAdIndex(adIndex + 1)
      if (needsRecalibrationRef.current) { needsRecalibrationRef.current = false; beginCamera(true) }
      else setPhase('neutral')
    }
    else setPhase('beliefs')
  }
  const moveRanking = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= beliefs.ranking.length) return
    const ranking = [...beliefs.ranking]
    ;[ranking[index], ranking[target]] = [ranking[target], ranking[index]]
    setBeliefs((previous) => ({ ...previous, ranking }))
  }
  const save = async () => {
    if (Object.keys(beliefs.scales).length !== BELIEF_QUESTIONS.length) return setError('Одговорете на сите тврдења.')
    setError(''); setPhase('saving')
    try {
      stopTracker()
      if (exposuresRef.current.length !== adOrder.length) throw new Error('Missing advertisement exposure')
      await faceRecording.ensureUploaded(exposuresRef.current)
      const answerLabels = {
        'Возраст': demographics.age,
        'Род': demographics.gender,
        'Место на живеење': demographics.population,
        'Статус': demographics.status,
        ...Object.fromEntries(PLATFORMS.map((platform) => [`Z1 ${platform} (минути)`, habits.minutes[platform]])),
        'Z2 Кратки видеа': habits.shortVideos,
        'Z3 Содржини': habits.topics,
        'Z4 Купување преку социјални мрежи': habits.socialPurchase === 'yes' ? 'Да' : 'Не',
        'Z5 Онлајн купувања (%)': habits.onlinePercent,
        ...Object.fromEntries(adResults.flatMap((ad) => [
          ...AD_QUESTIONS.map((question, index) => [`${ad.advertisement_id} A${index + 1} ${question}`, ad.answers.scales[index]]),
          [`${ad.advertisement_id} A7 Споделување`, ad.answers.share === 'yes' ? 'Да' : 'Не'],
          [`${ad.advertisement_id} A8 Впечаток`, ad.answers.impression],
          ...IMPLICIT_ATTRIBUTES.map((attribute) => [`${ad.advertisement_id} Имплицитно: ${attribute.label} (совпаѓања)`, ad.implicit_trials.filter((trial) => trial.attribute === attribute.label && trial.response === 'matches').length]),
          [`${ad.advertisement_id} Имплицитно: просечно време (ms)`, ad.implicit_summary.mean_on_time_rt_ms],
        ])),
        ...Object.fromEntries(BELIEF_QUESTIONS.map((question, index) => [`B${index + 1} ${question}`, beliefs.scales[index]])),
        'B5 Извори според доверба': beliefs.ranking,
      }
      const answers = {
        AGE: Number(demographics.age), GENDER: demographics.gender, POPULATION: demographics.population, STATUS: demographics.status,
        ...Object.fromEntries(PLATFORMS.map((platform) => [`Z1_${platform.toUpperCase()}`, Number(habits.minutes[platform])])),
        Z2: Number(habits.shortVideos), Z3: habits.topics, Z4: habits.socialPurchase, Z5: habits.onlinePercent,
        ...Object.fromEntries(adResults.flatMap((ad) => [
          ...AD_QUESTIONS.map((_, index) => [`${ad.advertisement_id.toUpperCase()}_A${index + 1}`, Number(ad.answers.scales[index])]),
          [`${ad.advertisement_id.toUpperCase()}_A7`, ad.answers.share],
          [`${ad.advertisement_id.toUpperCase()}_A8`, ad.answers.impression],
          ...IMPLICIT_ATTRIBUTES.map((attribute, index) => [`${ad.advertisement_id.toUpperCase()}_IMP${index + 1}`, ad.implicit_trials.filter((trial) => trial.attribute === attribute.label && trial.response === 'matches').length]),
          [`${ad.advertisement_id.toUpperCase()}_RT`, ad.implicit_summary.mean_on_time_rt_ms],
        ])),
        ...Object.fromEntries(BELIEF_QUESTIONS.map((_, index) => [`B${index + 1}`, Number(beliefs.scales[index])])),
        B5: beliefs.ranking.join(' > '),
      }
      await saveSurveyResult({ surveyCode: SURVEY_CODE, responseId, result: {
        survey_code: SURVEY_CODE, response_id: responseId, status: 'completed', language: 'mk', country: 'MK', completed_at: new Date().toISOString(),
        consent: { age_18_or_over: true, camera_tracking: true, camera_video_storage: true, privacy_policy: true }, demographics, media_habits: habits,
        advertisement_order: adOrder.map((ad) => ad.id), advertisement_tests: adResults, beliefs, answers, answer_labels: answerLabels,
        eye_tracking: { engine: 'webgazer', calibration: calibrationRef.current, ad_exposures: exposuresRef.current, emotion_analysis_status: 'not_available', aoi_status: 'not_defined' },
        implicit_test: { status: 'completed', paradigm: 'advertisement_attribute_match_rt', deadline_ms: IMPLICIT_DEADLINE_MS, attributes: IMPLICIT_ATTRIBUTES.map((attribute) => attribute.label), score_note: 'Response times and match choices are recorded; a conventional IAT D-score requires contrasted category blocks.' },
      } })
      setPhase('complete')
    } catch (e) { setError('Одговорите не може да се зачуваат. Проверете ја интернет-врската и обидете се повторно.'); setPhase('beliefs') }
  }

  const screen = (title, body, action, label) => <main className="mk-screen"><section><div className="mk-kicker">НЕВРОАНКЕТА · ГЕНЕРАЦИЈА Z</div><h1>{title}</h1>{body}{error && <p className="mk-error" role="alert">{error}</p>}{action && <button className="mk-primary" onClick={action}>{label || 'Продолжи'}</button>}</section></main>
  const yesNo = (name, value, onChange) => <div className="mk-options">{[['yes', 'Да'], ['no', 'Не']].map(([key, label]) => <label key={key}><input type="radio" name={name} checked={value === key} onChange={() => onChange(key)} />{label}</label>)}</div>

  if (phase === 'consent') return screen('Согласност и подготовка', <><p>Ве молиме прочитајте и одговорете пред да започнете.</p>{[
    ['age', 'Имам 18 години или повеќе.'],
    ['camera', 'Се согласувам веб-камерата на мојот компјутер да се користи за следење на погледот и снимање на лицето додека се прикажуваат рекламите. Видеото ќе се зачува за подоцнежна истражувачка обработка, заедно со изведените координати на погледот.'],
    ['privacy', 'Се согласувам со политиката за приватност и начинот на постапување со податоците.'],
  ].map(([key, text]) => <fieldset className="mk-fieldset" key={key}><legend>{text} <b>*</b></legend>{yesNo(key, consent[key], (value) => setConsent({ ...consent, [key]: value }))}</fieldset>)}</>, validateConsent, 'Продолжи')
  if (phase === 'ineligible') return screen('Ви благодариме', <p>Не можете да продолжите со ова истражување врз основа на дадените одговори.</p>)
  if (phase === 'camera-check') return <main className="mk-screen mk-camera-check"><section><div className="mk-kicker">НЕВРОАНКЕТА · ГЕНЕРАЦИЈА Z</div><h1>Проверка на камерата</h1><p>Поставете го лицето во центарот на сликата. Обезбедете добро осветлување и седнете на 60–90 см од екранот.</p>{cameraState === 'loading' && <p>Се вклучува камерата…</p>}{cameraState === 'ready' && <><p className={faceSeen ? 'mk-face-ok' : 'mk-face-wait'}>{faceSeen ? 'Вашето лице е откриено. Може да продолжите.' : 'Се бара Вашето лице во кадарот…'}</p><div className="mk-camera-space" /><button className="mk-primary" disabled={!faceSeen} onClick={startCalibration}>Започни калибрација</button></>}{cameraState === 'error' && <><p className="mk-error">{error}</p><button className="mk-primary" onClick={() => beginCamera(recalibration)}>Обидете се повторно</button></>}</section></main>
  if (phase === 'calibration') return <main className="mk-calibration"><div className="mk-calibration-card"><h1>Поставување и калибрација</h1><p>Обезбедете добро осветлување, избегнувајте очила со рефлектирачки стакла, седнете на 60–90 см од екранот и држете ја главата мирно.</p>{cameraState === 'loading' && <p>Се вклучува камерата…</p>}{cameraState === 'error' && <><p className="mk-error">{error}</p><button onClick={() => beginCamera(recalibration)}>Обидете се повторно</button></>}{cameraState === 'ready' && <p>{faceSeen ? 'Кликнете пет пати на секоја точка додека гледате во неа.' : 'Поставете го лицето во кадарот на камерата.'} · {calibrationCounts.reduce((a, b) => a + b, 0)}/{POINTS.length * CLICKS_PER_POINT}</p>}</div>{cameraState === 'ready' && POINTS.map(([x, y], index) => <button key={index} className="mk-point" style={{ left: `${x}%`, top: `${y}%` }} disabled={!faceSeen || calibrationCounts[index] >= CLICKS_PER_POINT} onClick={(event) => clickPoint(index, event)} aria-label={`Точка ${index + 1}`}>{calibrationCounts[index] >= CLICKS_PER_POINT ? '✓' : CLICKS_PER_POINT - calibrationCounts[index]}</button>)}</main>
  if (phase === 'demographics') return screen('Почетни прашања и демографски податоци', <div className="mk-form"><label>Земја<input value="Северна Македонија" readOnly /></label><label>Возраст (18–29)<input type="number" min="18" max="29" value={demographics.age} onChange={(e) => setDemographics({ ...demographics, age: e.target.value })} /></label>{[
    ['gender', 'Род', ['Женски', 'Машки', 'Небинарен', 'Не сакам да одговорам']],
    ['population', 'Број на жители во местото на живеење', ['Помалку од 50.000', '50.000–200.000', '200.000–1.000.000', 'Повеќе од 1.000.000']],
    ['status', 'Статус', ['Студент/ка', 'Вработен/а', 'Самовработен/а', 'Невработен/а', 'Друго']],
  ].map(([key, label, options]) => <label key={key}>{label}<select value={demographics[key]} onChange={(e) => setDemographics({ ...demographics, [key]: e.target.value })}><option value="">Изберете</option>{options.map((option) => <option key={option}>{option}</option>)}</select></label>)}</div>, validateDemographics)
  if (phase === 'habits') return screen('Медиумски навики', <div className="mk-form"><p>Z1. Колку минути дневно ги користите следниве платформи?</p><div className="mk-grid">{PLATFORMS.map((platform) => <label key={platform}>{platform}<input type="number" min="0" max="1440" value={habits.minutes[platform] ?? ''} onChange={(e) => setHabits({ ...habits, minutes: { ...habits.minutes, [platform]: e.target.value } })} /></label>)}</div><Scale questions={['Најмногу гледам кратки вертикални видеа (Reels/TikTok).']} answers={{ 0: habits.shortVideos }} onChange={(_, value) => setHabits({ ...habits, shortVideos: value })} prefix="Z2" /><fieldset className="mk-fieldset"><legend>Z3. Редовно следам (изберете повеќе одговори).</legend><div className="mk-checks">{TOPICS.map((topic) => <label key={topic}><input type="checkbox" checked={habits.topics.includes(topic)} onChange={(e) => setHabits({ ...habits, topics: e.target.checked ? [...habits.topics, topic] : habits.topics.filter((item) => item !== topic) })} />{topic}</label>)}</div></fieldset><fieldset className="mk-fieldset"><legend>Z4. Во последните 30 дена купив нешто што првпат го видов на социјалните мрежи.</legend>{yesNo('socialPurchase', habits.socialPurchase, (value) => setHabits({ ...habits, socialPurchase: value }))}</fieldset><label>Z5. Колкав процент од Вашите купувања се онлајн? <strong>{habits.onlinePercent}%</strong><input type="range" min="0" max="100" value={habits.onlinePercent} onChange={(e) => setHabits({ ...habits, onlinePercent: Number(e.target.value) })} /></label></div>, validateHabits)
  if (phase === 'ad-intro') return screen('Реклами', <p>Ќе погледнете {adOrder.length} реклами по случаен редослед. По секоја реклама следува краток тест за совпаѓање на зборови, а потоа неколку прашања. Гледајте во екранот и држете ја главата мирно.</p>, beginAd, 'Прикажи реклама')
  if (phase === 'neutral') return screen('Кратка пауза', <p>Одморете го погледот на празен екран пред следната реклама.</p>, beginAd, 'Продолжи кон следната реклама')
  if (phase === 'ad') return <main className="mk-video"><video ref={videoRef} src={currentAd.url} autoPlay playsInline onPlay={onAdPlay} onEnded={finishAd} onError={() => { faceRecording.abort(); exposureRef.current = null; setError('Рекламата не може да се вчита. Обидете се повторно.'); setPhase('ad-intro') }} />{!videoStarted && <button className="mk-play" onClick={() => videoRef.current?.play()}>Пушти ја рекламата</button>}<span>Реклама {adIndex + 1} од {adOrder.length}</span></main>
  if (phase === 'implicit-intro') return screen('Краток тест на реакција', <><p>За секој збор одлучете дали одговара на впечатокот што Ви го остави рекламата што штотуку ја гледавте.</p><p>Одговорете што е можно побрзо и поточно. Користете ги копчињата на екранот или тастерите <strong>Е</strong> (не се совпаѓа) и <strong>И</strong> (се совпаѓа).</p><p>Зборовите се поврзани со: <strong>Автентичен, Модерен, Достоен за доверба.</strong></p></>, beginImplicitTest, 'Започни тест')
  if (phase === 'implicit-fixation' || phase === 'implicit-gap') return <main className="mk-implicit-blank" aria-hidden="true">{phase === 'implicit-fixation' ? '+' : ''}</main>
  if (phase === 'implicit-stimulus') return <main className="mk-implicit"><div className="mk-implicit-progress">{implicitIndex + 1} / {currentImplicitTrials.length}</div><p>Дали зборот одговара на рекламата?</p><h1>{currentImplicitTrials[implicitIndex].stimulus}</h1><div className="mk-implicit-actions"><button onClick={() => respondImplicit('does_not_match')}>НЕ СЕ СОВПАЃА <small>Е</small></button><button onClick={() => respondImplicit('matches')}>СЕ СОВПАЃА <small>И</small></button></div><div className="mk-implicit-hint">{implicitLate ? 'Обидете се да одговорите побрзо на следниот збор.' : 'Одговорете брзо и точно.'}</div></main>
  if (phase === 'ad-questions') return screen(`Прашања по рекламата ${adIndex + 1}`, <><Scale questions={AD_QUESTIONS} answers={adAnswers.scales} onChange={(index, value) => setAdAnswers({ ...adAnswers, scales: { ...adAnswers.scales, [index]: value } })} prefix="A" /><fieldset className="mk-fieldset"><legend>A7. Би ја споделил/а оваа реклама со пријателите.</legend>{yesNo('share', adAnswers.share, (value) => setAdAnswers({ ...adAnswers, share: value }))}</fieldset><label className="mk-label">A8. Што Ви остави најголем впечаток?<input maxLength="300" value={adAnswers.impression} onChange={(e) => setAdAnswers({ ...adAnswers, impression: e.target.value })} /></label></>, saveAdQuestions)
  if (phase === 'beliefs') return screen('Уверувања и ставови', <><Scale questions={BELIEF_QUESTIONS} answers={beliefs.scales} onChange={(index, value) => setBeliefs({ ...beliefs, scales: { ...beliefs.scales, [index]: value } })} prefix="B" /><h2>B5. Подредете ги изворите според довербата</h2><p>Најдоверливиот извор поставете го прв.</p><ol className="mk-ranking">{beliefs.ranking.map((source, index) => <li key={source}>{source}<span><button type="button" onClick={() => moveRanking(index, -1)} disabled={index === 0} aria-label={`${source} погоре`}>↑</button><button type="button" onClick={() => moveRanking(index, 1)} disabled={index === beliefs.ranking.length - 1} aria-label={`${source} подолу`}>↓</button></span></li>)}</ol></>, save, 'Заврши')
  if (phase === 'recording-saving') return screen('Се зачувува снимката од камерата', <p>Ве молиме почекајте…</p>)
  if (phase === 'saving') return screen('Се зачувува', <p>Ве молиме почекајте…</p>)
  return screen('Ви благодариме', <p>Вашите одговори се зачувани.</p>)
}
